import { Form as InertiaForm, Link, router } from '@inertiajs/react'
import { useMemo, useRef, useState } from 'react'
import { AssessmentType, PaginationType, PaymentType, TaxPayerType, TaxRuleOptionType } from './types.ts'
import {Button} from "@/components/ui/button.tsx";
import {
  AlertCircle,
  ArrowLeft, Building2, Calendar,
  CheckCircle,
  Clock,
  DollarSign,
  Edit,
  FileText,
  Mail,
  MapPin,
  Phone,
  Printer,
  Trash2
} from "lucide-react";
import {Card} from "@/components/ui/card.tsx";
import {Tabs, TabsContent, TabsList, TabsTrigger} from "@/components/ui/tabs.tsx";
import Pagination from "@/components/pagination.tsx";

interface ShowProps {
  taxpayer: TaxPayerType
  due_balance?: number | string
  total_paid?: number | string
  assessments?: AssessmentType[]
  tax_rule_options?: TaxRuleOptionType[]
  payments?: PaymentType[]
  payments_pagination?: PaginationType
  active_tab?: string | null
  can_edit?: boolean
  can_destroy?: boolean
  can_destroy_assessments?: boolean
}

const formatRuleType = (ruleType?: string): string => {
  if (ruleType === "percentage") return "Pourcentage"
  if (ruleType === "per_item") return "Par nombre d'elements"
  return "Montant fixe"
}

const formatMoney = (value?: number | string | null): string =>
  `${Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} FCFA`

const formatDate = (value?: string | null): string =>
  value ? new Date(value).toLocaleDateString() : "-"

const escapeHtml = (value?: string | number | null): string =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;")

export default function Show({
  taxpayer,
  due_balance = 0,
  total_paid = 0,
  assessments = [],
  tax_rule_options = [],
  payments = [],
  payments_pagination,
  active_tab,
  can_edit = false,
  can_destroy = false,
  can_destroy_assessments = false,
}: ShowProps) {
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false)
  const [isCollectModalOpen, setIsCollectModalOpen] = useState(false)
  const [selectedTaxId, setSelectedTaxId] = useState('')
  const [selectedTaxRuleId, setSelectedTaxRuleId] = useState('')
  const [itemCount, setItemCount] = useState('1')
  const [selectedAssessmentId, setSelectedAssessmentId] = useState('')
  const [paymentAmount, setPaymentAmount] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [collectedAt, setCollectedAt] = useState('')
  const dueDateInputRef = useRef<HTMLInputElement | null>(null)
  const latitude = Number(taxpayer.lat)
  const longitude = Number(taxpayer.lng)
  const hasValidCoordinates = Number.isFinite(latitude) && Number.isFinite(longitude)
  const mapDelta = 0.01
  const mapEmbedUrl = hasValidCoordinates
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${longitude - mapDelta}%2C${latitude - mapDelta}%2C${longitude + mapDelta}%2C${latitude + mapDelta}&layer=mapnik&marker=${latitude}%2C${longitude}`
    : ''
  const openStreetMapUrl = hasValidCoordinates
    ? `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=16/${latitude}/${longitude}`
    : ''
  const taxOptions = useMemo(
    () =>
      Array.from(
        new Map(
          tax_rule_options.map((rule) => [
            rule.tax_id,
            {
              id: rule.tax_id,
              name: rule.tax_name || rule.tax_code || 'Taxe',
              code: rule.tax_code || '',
            },
          ])
        ).values()
      ),
    [tax_rule_options]
  )
  const filteredTaxRuleOptions = useMemo(
    () => (selectedTaxId ? tax_rule_options.filter((rule) => rule.tax_id === selectedTaxId) : []),
    [tax_rule_options, selectedTaxId]
  )
  const selectedTaxRule = useMemo(
    () => tax_rule_options.find((rule) => rule.id === selectedTaxRuleId),
    [tax_rule_options, selectedTaxRuleId]
  )
  const itemCountNumber = Number(itemCount)
  const selectedRuleAmount = selectedTaxRule ? Number(selectedTaxRule.amount) : 0
  const perItemPreview = selectedTaxRule?.formula_type === 'per_item' && Number.isFinite(itemCountNumber) && itemCountNumber > 0
    ? selectedRuleAmount * itemCountNumber
    : null
  const payableAssessments = useMemo(
    () =>
      assessments.filter(
        (assessment) =>
          !['paid', 'canceled'].includes(assessment.status) && Number(assessment.balance_due || 0) > 0
      ),
    [assessments]
  )
  const selectedAssessment = useMemo(
    () => payableAssessments.find((assessment) => assessment.id === selectedAssessmentId),
    [payableAssessments, selectedAssessmentId]
  )
  const totalAssessmentAmount = useMemo(
    () => assessments.reduce((sum, assessment) => sum + Number(assessment.total_amount || 0), 0),
    [assessments]
  )
  const printAssessmentsBill = () => {
    const printedAt = new Date()
    const rows = assessments
      .map((assessment, index) => {
        const taxLabel = assessment.tax_name || assessment.tax_code || "Taxe"

        return `
          <tr>
            <td>${index + 1}</td>
            <td>
              <strong>${escapeHtml(taxLabel)}</strong>
              <span>${escapeHtml(assessment.tax_rule_code || "-")} - ${escapeHtml(formatRuleType(assessment.tax_rule_formula_type || undefined))}</span>
            </td>
            <td>${escapeHtml(assessment.period_name || "-")}</td>
            <td>${escapeHtml(formatDate(assessment.due_date))}</td>
            <td>${escapeHtml(assessment.status)}</td>
            <td class="amount">${escapeHtml(formatMoney(assessment.total_amount))}</td>
            <td class="amount">${escapeHtml(formatMoney(assessment.balance_due))}</td>
          </tr>
        `
      })
      .join("")
    const billWindow = window.open("", "_blank", "width=960,height=720")

    if (!billWindow) return

    billWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>Facture - ${escapeHtml(taxpayer.code)}</title>
          <style>
            * { box-sizing: border-box; }
            body { color: #111827; font-family: Arial, sans-serif; margin: 32px; }
            header { border-bottom: 2px solid #111827; display: flex; justify-content: space-between; gap: 24px; padding-bottom: 18px; }
            h1 { font-size: 24px; margin: 0 0 8px; }
            h2 { font-size: 16px; margin: 24px 0 10px; }
            p { margin: 3px 0; }
            .muted { color: #4b5563; font-size: 12px; }
            .grid { display: grid; gap: 18px; grid-template-columns: 1fr 1fr; margin-top: 22px; }
            .box { border: 1px solid #d1d5db; padding: 14px; }
            table { border-collapse: collapse; margin-top: 10px; width: 100%; }
            th, td { border: 1px solid #d1d5db; font-size: 12px; padding: 9px; text-align: left; vertical-align: top; }
            th { background: #f3f4f6; }
            td span { color: #4b5563; display: block; font-size: 11px; margin-top: 3px; }
            .amount { text-align: right; white-space: nowrap; }
            .summary { margin-left: auto; margin-top: 18px; width: 320px; }
            .summary div { display: flex; justify-content: space-between; padding: 7px 0; }
            .summary .total { border-top: 2px solid #111827; font-weight: 700; }
            footer { border-top: 1px solid #d1d5db; color: #4b5563; font-size: 11px; margin-top: 36px; padding-top: 12px; }
            @media print { body { margin: 18mm; } button { display: none; } }
          </style>
        </head>
        <body>
          <header>
            <div>
              <h1>Facture des taxes</h1>
              <p><strong>${escapeHtml(taxpayer.community_name || taxpayer.community_id)}</strong></p>
              <p class="muted">Generee le ${escapeHtml(printedAt.toLocaleString())}</p>
            </div>
            <div>
              <p><strong>No contribuable:</strong> ${escapeHtml(taxpayer.code)}</p>
              <p><strong>Solde:</strong> ${escapeHtml(formatMoney(due_balance))}</p>
            </div>
          </header>

          <section class="grid">
            <div class="box">
              <h2>Contribuable</h2>
              <p><strong>Nom:</strong> ${escapeHtml(taxpayer.name)}</p>
              <p><strong>Nom complet:</strong> ${escapeHtml(taxpayer.full_name)}</p>
              <p><strong>Type:</strong> ${escapeHtml(taxpayer.tax_payer_type_name || taxpayer.tax_payer_type_id)}</p>
              <p><strong>NIF:</strong> ${escapeHtml(taxpayer.nif || "-")}</p>
              <p><strong>RCCM:</strong> ${escapeHtml(taxpayer.rccm || "-")}</p>
            </div>
            <div class="box">
              <h2>Contact</h2>
              <p><strong>Telephone:</strong> ${escapeHtml(taxpayer.phone || "-")}</p>
              <p><strong>Email:</strong> ${escapeHtml(taxpayer.email || "-")}</p>
              <p><strong>Zone:</strong> ${escapeHtml(taxpayer.area_name || taxpayer.area_id)}</p>
              <p><strong>Adresse:</strong> ${escapeHtml(taxpayer.address || "-")}</p>
            </div>
          </section>

          <section>
            <h2>Assessments</h2>
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Taxe</th>
                  <th>Periode</th>
                  <th>Echeance</th>
                  <th>Statut</th>
                  <th class="amount">Montant</th>
                  <th class="amount">Solde</th>
                </tr>
              </thead>
              <tbody>
                ${rows || '<tr><td colspan="7">Aucune taxe affectee.</td></tr>'}
              </tbody>
            </table>
          </section>

          <section class="summary">
            <div><span>Total facture</span><strong>${escapeHtml(formatMoney(totalAssessmentAmount))}</strong></div>
            <div><span>Total paye</span><strong>${escapeHtml(formatMoney(total_paid))}</strong></div>
            <div class="total"><span>Solde a payer</span><strong>${escapeHtml(formatMoney(due_balance))}</strong></div>
          </section>

          <footer>
            Document genere depuis Taxio pour les affectations fiscales du contribuable.
          </footer>

          <script>
            window.addEventListener("load", () => {
              window.print();
              window.close();
            });
          </script>
        </body>
      </html>
    `)
    billWindow.document.close()
  }
  const handleDestroy = () => {
    if (!window.confirm(`Supprimer le contribuable "${taxpayer.name}" ?`)) return

    router.delete(`/tax_payers/${taxpayer.id}`)
  }
  const handleDestroyAssessment = (assessment: AssessmentType) => {
    const label = assessment.tax_name || assessment.tax_code || assessment.id
    if (!window.confirm(`Supprimer l'affectation "${label}" ?`)) return

    router.delete(`/tax_payers/${taxpayer.id}/assessments/${assessment.id}`, {
      preserveScroll: true,
    })
  }

  return (
    <>

      <div className="flex flex-col gap-6 p-8">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link href="/tax_payers">
              <Button variant="outline" size="icon">
                <ArrowLeft className="h-4 w-4" />
              </Button>
            </Link>
            <div>
              <h1 className="text-3xl font-bold text-foreground">{taxpayer.name}</h1>
              <p className="text-sm text-muted-foreground">ID contribuable : {taxpayer.code}</p>
            </div>
          </div>
          {(can_edit || can_destroy || assessments.length > 0) && (
            <div className="flex items-center gap-2">
              {assessments.length > 0 && (
                <Button variant="outline" className="gap-2 bg-transparent" onClick={printAssessmentsBill}>
                  <Printer className="h-4 w-4" />
                  Imprimer la facture
                </Button>
              )}
              {can_edit && (
                <>
                  <Button
                    className="gap-2"
                    onClick={() => {
                      const firstAssessment = payableAssessments[0]
                      setSelectedAssessmentId(firstAssessment?.id || '')
                      setPaymentAmount(firstAssessment ? String(firstAssessment.balance_due) : '')
                      setCollectedAt('')
                      setIsCollectModalOpen(true)
                    }}
                    disabled={payableAssessments.length === 0}
                  >
                      <DollarSign className="h-4 w-4" />
                      Encaisser un paiement
                  </Button>
                  <Link href={`/tax_payers/${taxpayer.id}/edit`}>
                    <Button variant="outline" className="gap-2 bg-transparent">
                      <Edit className="h-4 w-4" />
                      Modifier
                    </Button>
                  </Link>
                </>
              )}
              {can_destroy && (
                <Button variant="outline" className="gap-2 text-destructive hover:text-destructive" onClick={handleDestroy}>
                  <Trash2 className="h-4 w-4" />
                  Supprimer
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Overview Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card className="border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Solde restant</p>
                <p className="mt-2 text-2xl font-bold text-warning">{Number(due_balance || 0).toFixed(2)}</p>
              </div>
              <AlertCircle className="h-8 w-8 text-warning" />
            </div>
          </Card>

          <Card className="border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total payé</p>
                <p className="mt-2 text-2xl font-bold text-success">{Number(total_paid || 0).toFixed(2)}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-success" />
            </div>
          </Card>

          <Card className="border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Taxes actives</p>
                <p className="mt-2 text-2xl font-bold text-foreground">0</p>
              </div>
              <FileText className="h-8 w-8 text-primary" />
            </div>
          </Card>

          <Card className="border-border bg-card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Statut</p>
                {/*<Badge className="mt-2" variant={taxpayer.status === "overdue" ? "destructive" : "default"}>*/}
                {/*  {taxpayer.status}*/}
                {/*</Badge>*/}
              </div>
              <Clock className="h-8 w-8 text-muted-foreground" />
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <div className="grid gap-6 lg:grid-cols-3">
          {/* Left Column - Contact & Location */}
          <div className="space-y-6">
            <Card className="border-border bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold text-foreground">Informations de contact</h3>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Phone className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Téléphone</p>
                    <p className="font-medium text-foreground">{taxpayer.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Email</p>
                    <p className="font-medium text-foreground">{taxpayer.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Type de contribuable</p>
                    <p className="font-medium text-foreground">
                      {taxpayer.tax_payer_type_name || taxpayer.tax_payer_type_id}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Zone</p>
                    <p className="font-medium text-foreground">
                      {taxpayer.area_name || taxpayer.area_id}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Adresse</p>
                    <p className="font-medium text-foreground">{taxpayer.address}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Building2 className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Communauté</p>
                    <p className="font-medium text-foreground">
                      {taxpayer.community_name || taxpayer.community_id}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Enregistré le</p>
                    <p className="font-medium text-foreground">
                      {new Date(taxpayer.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="border-border bg-card p-6">
              <h3 className="mb-4 text-lg font-semibold text-foreground">GPS Location</h3>
              <div className="relative h-[300px] overflow-hidden rounded-lg border border-border bg-secondary">
                {hasValidCoordinates ? (
                  <iframe
                    title={`Map for ${taxpayer.name}`}
                    src={mapEmbedUrl}
                    className="h-full w-full"
                    loading="lazy"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <MapPin className="mx-auto mb-2 h-12 w-12 text-primary" />
                      <p className="text-sm text-muted-foreground">No location coordinates available</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="mt-3 flex items-center justify-between gap-2">
                <p className="font-mono text-xs text-muted-foreground">
                  {hasValidCoordinates ? `${latitude.toFixed(6)}, ${longitude.toFixed(6)}` : 'Lat/Lng not set'}
                </p>
                {hasValidCoordinates && (
                  <a href={openStreetMapUrl} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="bg-transparent">
                      Voir on Map
                    </Button>
                  </a>
                )}
              </div>
            </Card>
          </div>

          {/* Right Column - Tabs */}
          <div className="lg:col-span-2">
            <Tabs defaultValue={active_tab === "payments" ? "payments" : "taxes"} className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="taxes">Affectations de taxes</TabsTrigger>
                <TabsTrigger value="payments">Historique des paiements</TabsTrigger>
                <TabsTrigger value="documents">Documents</TabsTrigger>
              </TabsList>

              <TabsContent value="taxes" className="mt-6">
                <Card className="border-border bg-card p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-foreground">Taxes affectées</h3>
                    {can_edit && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedTaxId('')
                          setSelectedTaxRuleId('')
                          setItemCount('1')
                          setDueDate('')
                          setIsAssignModalOpen(true)
                        }}
                      >
                        Assigner une nouvelle taxe
                      </Button>
                    )}
                  </div>
                  <div className="space-y-3">
                    {assessments.length === 0 && (
                      <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
                        No assessments assigned yet.
                      </div>
                    )}
                    {assessments.map((assessment) => (
                      <div key={assessment.id} className="rounded-lg border border-border p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <p className="font-semibold text-foreground">
                              {assessment.tax_name || assessment.tax_code || "Taxe"}
                            </p>
                            <p className="mt-1 text-sm text-muted-foreground">
                              Rule: {assessment.tax_rule_code || "-"} ({assessment.tax_rule_formula_type || "-"})
                            </p>
                            <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                              <span>Statut : {assessment.status}</span>
                              <span>Balance: {assessment.balance_due}</span>
                              {assessment.due_date && (
                                <span>Due: {new Date(assessment.due_date).toLocaleDateString()}</span>
                              )}
                              {assessment.period_name && <span>Period: {assessment.period_name}</span>}
                            </div>
                          </div>
                          <div className="flex items-start gap-2 text-right">
                            <p className="text-xl font-bold text-foreground">{assessment.total_amount}</p>
                            {can_destroy_assessments && (
                              <Button
                                variant="ghost"
                                size="icon-sm"
                                className="text-destructive hover:text-destructive"
                                aria-label={`Supprimer l'affectation ${assessment.tax_name || assessment.tax_code || assessment.id}`}
                                onClick={() => handleDestroyAssessment(assessment)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="payments" className="mt-6">
                <Card className="border-border bg-card p-6">
                  <h3 className="mb-4 text-lg font-semibold text-foreground">Historique des paiements</h3>
                  <div className="space-y-3">
                    {payments.length === 0 && (
                      <div className="rounded-lg border border-dashed border-border p-4 text-sm text-muted-foreground">
                        No payments recorded yet.
                      </div>
                    )}

                    {payments.map((payment) => {
                      const paymentDate = payment.collected_at || payment.created_at

                      return (
                        <div key={payment.id} className="rounded-lg border border-border p-4">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <p className="font-semibold text-foreground">
                                {payment.tax_name || payment.tax_code || "Paiement"}
                              </p>
                              <p className="mt-1 font-mono text-xs text-muted-foreground">ID: {payment.id}</p>
                              <div className="mt-2 flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
                                <span>{new Date(paymentDate).toLocaleString()}</span>
                                <span>Method: {payment.payment_method || "-"}</span>
                                <span>Agent : {payment.collected_by_name || "-"}</span>
                                {payment.tax_rule_code && <span>Rule: {payment.tax_rule_code}</span>}
                                {payment.assessment_code && <span>Assessment: {payment.assessment_code}</span>}
                              </div>
                            </div>
                            <div className="text-right">
                              <p className="text-xl font-bold text-success">
                                {Number(payment.amount || 0).toFixed(2)}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {payment.sync ? "Synced" : "Pending sync"}
                              </p>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>

                  {payments_pagination && payments_pagination.total_pages > 1 && (
                    <div className="mt-6">
                      <Pagination
                        currentPage={payments_pagination.current_page}
                        totalPages={payments_pagination.total_pages}
                        basePath={`/tax_payers/${taxpayer.id}`}
                        query={{ tab: "payments" }}
                      />
                    </div>
                  )}
                </Card>
              </TabsContent>

              <TabsContent value="documents" className="mt-6">
                <Card className="border-border bg-card p-6">
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-lg font-semibold text-foreground">Documents</h3>
                    <Button size="sm" variant="outline">
                      Upload Document
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {/*{documents.map((doc) => (*/}
                    {/*    <div key={doc.id} className="flex items-center justify-between rounded-lg border border-border p-4">*/}
                    {/*      <div className="flex items-center gap-3">*/}
                    {/*        <FileText className="h-8 w-8 text-primary" />*/}
                    {/*        <div>*/}
                    {/*          <p className="font-medium text-foreground">{doc.name}</p>*/}
                    {/*          <p className="text-sm text-muted-foreground">*/}
                    {/*            {doc.type} • {doc.size} • Uploaded {new Date(doc.uploadDate).toLocaleDateString()}*/}
                    {/*          </p>*/}
                    {/*        </div>*/}
                    {/*      </div>*/}
                    {/*      <Button size="sm" variant="outline">*/}
                    {/*        Download*/}
                    {/*      </Button>*/}
                    {/*    </div>*/}
                    {/*))}*/}
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      {isAssignModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-4"
          onClick={() => setIsAssignModalOpen(false)}
        >
          <div
            className="w-full max-w-2xl rounded-lg bg-background p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-foreground">Assign Assessment</h3>
              <button
                type="button"
                onClick={() => setIsAssignModalOpen(false)}
                className="rounded-md border border-gray-300 px-3 py-1 text-sm"
              >
                Fermer
              </button>
            </div>

            <InertiaForm
              action={`/tax_payers/${taxpayer.id}/assessments`}
              method="post"
              transform={(data) => ({ assessment: data })}
              className="space-y-4"
              onSuccess={() => setIsAssignModalOpen(false)}
            >
              {({ errors, processing }) => (
                <>
                  <div>
                    <label htmlFor="tax_id" className="block font-medium mb-1">Taxe</label>
                    <select
                      id="tax_id"
                      name="tax_id"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2 mb-4"
                      required
                      value={selectedTaxId}
                      onChange={(e) => {
                        setSelectedTaxId(e.target.value)
                        setSelectedTaxRuleId('')
                        setItemCount('1')
                      }}
                    >
                      <option value="" disabled>Sélectionner une taxe</option>
                      {taxOptions.map((tax) => (
                        <option key={tax.id} value={tax.id}>
                          {tax.name}{tax.code ? ` (${tax.code})` : ''}
                        </option>
                      ))}
                    </select>
                    {errors.tax_id && (
                      <div className="text-red-500 px-3 py-2 font-medium">{errors.tax_id.join(', ')}</div>
                    )}

                    <label htmlFor="tax_rule_id" className="block font-medium mb-1">Règle de taxe</label>
                    <select
                      id="tax_rule_id"
                      name="tax_rule_id"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2"
                      required
                      value={selectedTaxRuleId}
                      onChange={(e) => {
                        setSelectedTaxRuleId(e.target.value)
                        setItemCount('1')
                      }}
                      disabled={!selectedTaxId}
                    >
                      <option value="" disabled>{selectedTaxId ? 'Sélectionnez une règle active' : 'Sélectionnez d\'abord une taxe'}</option>
                      {filteredTaxRuleOptions.map((rule) => (
                        <option key={rule.id} value={rule.id}>
                          {(rule.tax_name || rule.tax_code || "Taxe")} - {rule.code} ({formatRuleType(rule.formula_type)}) [par défaut : {rule.amount}]
                        </option>
                      ))}
                    </select>
                    {errors.tax_rule_id && (
                      <div className="text-red-500 px-3 py-2 font-medium">{errors.tax_rule_id.join(', ')}</div>
                    )}
                  </div>

                  {selectedTaxRule?.formula_type === 'per_item' && (
                    <div>
                      <label htmlFor="item_count" className="block font-medium mb-1">Nombre d'elements</label>
                      <input
                        type="number"
                        min="1"
                        step="1"
                        id="item_count"
                        name="item_count"
                        className="block w-full rounded-md border border-gray-300 px-3 py-2"
                        value={itemCount}
                        onChange={(e) => setItemCount(e.target.value)}
                        required
                      />
                      {perItemPreview !== null && (
                        <p className="mt-1 text-sm text-muted-foreground">
                          Total calcule: {perItemPreview.toLocaleString()} FCFA
                        </p>
                      )}
                      {errors.item_count && (
                        <div className="text-red-500 px-3 py-2 font-medium">{errors.item_count.join(', ')}</div>
                      )}
                    </div>
                  )}

                  <div>
                    <label htmlFor="total_amount" className="block font-medium mb-1">Montant (optionnel)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      id="total_amount"
                      name="total_amount"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2"
                      placeholder="Leave empty to use the tax rule amount"
                    />
                    {errors.total_amount && (
                      <div className="text-red-500 px-3 py-2 font-medium">{errors.total_amount.join(', ')}</div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="period_name" className="block font-medium mb-1">Period</label>
                    <input
                      type="text"
                      id="period_name"
                      name="period_name"
                      className="block w-full rounded-md border border-gray-300 px-3 py-2"
                      placeholder="e.g. Q1 2026"
                    />
                    {errors.period_name && (
                      <div className="text-red-500 px-3 py-2 font-medium">{errors.period_name.join(', ')}</div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="due_date" className="block font-medium mb-1">Due date</label>
                    <div className="flex items-center gap-2">
                      <input
                        ref={dueDateInputRef}
                        type="date"
                        id="due_date"
                        name="due_date"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        className="block w-full rounded-md border border-gray-300 px-3 py-2"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const input = dueDateInputRef.current as (HTMLInputElement & { showPicker?: () => void }) | null
                          input?.showPicker?.()
                          input?.focus()
                        }}
                      >
                        <Calendar className="h-4 w-4" />
                      </Button>
                    </div>
                    {errors.due_date && (
                      <div className="text-red-500 px-3 py-2 font-medium">{errors.due_date.join(', ')}</div>
                    )}
                  </div>

                  {tax_rule_options.length === 0 && (
                    <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 text-sm text-yellow-800">
                      No active tax rules available in this taxpayer&apos;s community.
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setIsAssignModalOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" disabled={processing || tax_rule_options.length === 0 || !selectedTaxId || !selectedTaxRuleId}>
                      {processing ? 'Assigning...' : 'Assign Assessment'}
                    </Button>
                  </div>
                </>
              )}
            </InertiaForm>
          </div>
        </div>
      )}

      {isCollectModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 px-4"
          onClick={() => setIsCollectModalOpen(false)}
        >
          <div
            className="w-full max-w-2xl rounded-lg bg-background p-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold text-foreground">Encaisser un paiement</h3>
              <button
                type="button"
                onClick={() => setIsCollectModalOpen(false)}
                className="rounded-md border border-border px-3 py-1 text-sm"
              >
                Fermer
              </button>
            </div>

            <InertiaForm
              action={`/tax_payers/${taxpayer.id}/payments`}
              method="post"
              transform={(data) => ({ payment: data })}
              className="space-y-4"
              onSuccess={() => setIsCollectModalOpen(false)}
            >
              {({ errors, processing }) => (
                <>
                  <div>
                    <label htmlFor="payment_assessment_id" className="mb-1 block font-medium">
                      Taxe à payer
                    </label>
                    <select
                      id="payment_assessment_id"
                      name="assessment_id"
                      className="block w-full rounded-md border border-border bg-background px-3 py-2"
                      required
                      value={selectedAssessmentId}
                      onChange={(e) => {
                        const assessmentId = e.target.value
                        const assessment = payableAssessments.find((item) => item.id === assessmentId)
                        setSelectedAssessmentId(assessmentId)
                        setPaymentAmount(assessment ? String(assessment.balance_due) : '')
                      }}
                    >
                      <option value="" disabled>
                        Sélectionner une taxe
                      </option>
                      {payableAssessments.map((assessment) => (
                        <option key={assessment.id} value={assessment.id}>
                          {assessment.tax_name || assessment.tax_code || 'Taxe'} - solde {assessment.balance_due}
                        </option>
                      ))}
                    </select>
                    {errors.assessment_id && (
                      <div className="px-3 py-2 font-medium text-destructive">{errors.assessment_id.join(', ')}</div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="payment_amount" className="mb-1 block font-medium">
                      Montant
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0.01"
                      max={selectedAssessment ? Number(selectedAssessment.balance_due || 0) : undefined}
                      id="payment_amount"
                      name="amount"
                      className="block w-full rounded-md border border-border bg-background px-3 py-2"
                      required
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                    />
                    {selectedAssessment && (
                      <p className="mt-1 text-sm text-muted-foreground">
                        Solde restant : {Number(selectedAssessment.balance_due || 0).toFixed(2)}
                      </p>
                    )}
                    {errors.amount && (
                      <div className="px-3 py-2 font-medium text-destructive">{errors.amount.join(', ')}</div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="payment_method" className="mb-1 block font-medium">
                      Méthode
                    </label>
                    <select
                      id="payment_method"
                      name="payment_method"
                      className="block w-full rounded-md border border-border bg-background px-3 py-2"
                      defaultValue="cash"
                    >
                      <option value="cash">Espèces</option>
                      <option value="mobile_money">Mobile money</option>
                      <option value="card">Carte</option>
                      <option value="bank_transfer">Virement bancaire</option>
                    </select>
                    {errors.payment_method && (
                      <div className="px-3 py-2 font-medium text-destructive">{errors.payment_method.join(', ')}</div>
                    )}
                  </div>

                  <div>
                    <label htmlFor="payment_collected_at" className="mb-1 block font-medium">
                      Date de paiement
                    </label>
                    <input
                      type="datetime-local"
                      id="payment_collected_at"
                      name="collected_at"
                      className="block w-full rounded-md border border-border bg-background px-3 py-2"
                      value={collectedAt}
                      onChange={(e) => setCollectedAt(e.target.value)}
                    />
                    {errors.collected_at && (
                      <div className="px-3 py-2 font-medium text-destructive">{errors.collected_at.join(', ')}</div>
                    )}
                  </div>

                  {payableAssessments.length === 0 && (
                    <div className="rounded-md border border-border px-3 py-2 text-sm text-muted-foreground">
                      Aucune taxe avec solde restant n'est disponible pour ce contribuable.
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-2">
                    <Button type="button" variant="outline" onClick={() => setIsCollectModalOpen(false)}>
                      Annuler
                    </Button>
                    <Button type="submit" disabled={processing || payableAssessments.length === 0 || !selectedAssessmentId || !paymentAmount}>
                      {processing ? 'Encaissement...' : 'Encaisser'}
                    </Button>
                  </div>
                </>
              )}
            </InertiaForm>
          </div>
        </div>
      )}
    </>
  )
}
